import { ActionCreatorWithPayload, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  experimentsAndInnovationsCategoryActions,
  experimentsAndInnovationsInitialState,
  experimentsAndInnovationsReducer,
  IExperimentsAndInnovationsFormData,
} from "./experiments-and-innovations/redux";
import { IOthersFormData, OthersCategoryActions, othersInitialState, othersReducer } from "./others/redux";
import { GITSCategoryActions, gitsInitialState, gitsReducer, IGITSFormData } from "./gits/redux";
import { PEV, ProgressErrorValue } from "../../utils/progress-error-value";
import * as service from "./service";
import { Nullable } from "../../utils/nullable";
import { ITeam } from "../team-details/redux";
import { INeoErrorInfo, NeoError } from "../../utils/errors/neo-errors";
import { createAsyncThunkWithRejectValue } from "../../common/redux/createAsyncThunkWithRejectValue";
import { ApplicationBasicInfo } from "../applications/application-details/redux";
import castTo, { identity } from "../../utils/cast-to";
import {
  getApplicationCategoryIfOthers,
  getGcpProjectNamesIfNeeded,
  getGithubOrganisationIfNeeded,
  getGitHubRepoIfNeeded,
  getTechStackIfOthers,
  isCiCdPipelineNeeded,
  isCodeRepositoryNeeded,
  isHostingServiceNeeded,
} from "./data/common-functions";
import { TeamType } from "../create-team/redux";
import { ApplicationPurpose } from "../../common/data/application-purpose";

export enum ProjectClassification {
  NONE = "NONE",
  EXPERIMENT_AND_INNOVATION = "EXPERIMENT_AND_INNOVATION",
  OTHERS = "OTHERS",
  GITS = "GITS",
}

export enum TeamStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export enum TeamStatusLabel {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
}

export type CurrentStep = "PROJECT_CLASSIFICATION" | "TEAM_SELECTION" | "BASIC_INFO" | "INFRA_CONFIG" | "REVIEW_SUBMIT";

export interface ICreateApplication {
  applicationId: string;
  applicationName: string;
}

export interface IGetStartedState {
  selectedProjectClassification: ProjectClassification;
  currentStep: CurrentStep;
  experimentsAndInnovations: IExperimentsAndInnovationsFormData;
  others: IOthersFormData;
  gits: IGITSFormData;
  createApplication: ProgressErrorValue<Nullable<ApplicationBasicInfo>>;
  teamListForSelectedCategoryPEV: ProgressErrorValue<ITeam[], Nullable<string>, Nullable<INeoErrorInfo>>;
  teamListForOthersPEV: ProgressErrorValue<ITeam[], Nullable<string>, Nullable<INeoErrorInfo>>;
  teamListForGITSPEV: ProgressErrorValue<ITeam[], Nullable<string>, Nullable<INeoErrorInfo>>;
  showCreateApplicationNotApplicablePage: boolean;
}

const initialState: IGetStartedState = {
  selectedProjectClassification: ProjectClassification.NONE,
  currentStep: "PROJECT_CLASSIFICATION",
  experimentsAndInnovations: experimentsAndInnovationsInitialState,
  others: othersInitialState,
  gits: gitsInitialState,
  createApplication: PEV(null),
  teamListForSelectedCategoryPEV: PEV([]),
  teamListForOthersPEV: PEV([]),
  teamListForGITSPEV: PEV([]),
  showCreateApplicationNotApplicablePage: false,
};

export type ApplicationFormData = IExperimentsAndInnovationsFormData | IGITSFormData | IOthersFormData;

export type ApplicationCreationRequest = {
  basicInfo: {
    applicationName: string;
    applicationDescription: string;
    country: string;
    targetAudience: string;
    businessFunction?: string;
    applicationCategory: string[];
    applicationOwner: string[];
    techStack?: string[];
    teamId: string;
    purpose: ApplicationPurpose | string;
    hasMnpi: boolean;
    hasPii: boolean;
    hasSensitivePii: boolean;
    endDate?: DateOnly;
    projectId?: string;
  };
  infraConfig: {
    isCodeRepositoryNeeded: boolean;
    githubRepo: string[];
    isCiCdPipelineNeeded: boolean;
    isHostingServiceNeeded: boolean;
    cloudProjectNames?: string[];
    billingProjectId?: string;
    comment: string;
    githubOrganisation: string;
  };
};

const createApplication = createAsyncThunk(
  "application/create",
  async ({ application, teamType }: { application: ApplicationFormData; teamType: TeamType }) => {
    const { endDate } = castTo<IExperimentsAndInnovationsFormData>(application).basicInfo;
    const applicationCreationRequestData = identity<ApplicationCreationRequest>({
      basicInfo: {
        applicationName: application.basicInfo.projectName,
        applicationDescription: application.basicInfo.projectDescription,
        purpose: application.basicInfo.purpose,
        country: application.basicInfo.countryCode,
        targetAudience: application.basicInfo.targetAudience,
        businessFunction: application.basicInfo.businessFunction ? application.basicInfo.businessFunction : undefined,
        applicationCategory: getApplicationCategoryIfOthers(application),
        applicationOwner: application.basicInfo.projectOwner,
        techStack: getTechStackIfOthers(application),
        teamId: application.basicInfo.teamId,
        hasMnpi: application.basicInfo.hasMnpi ?? false,
        hasPii: application.basicInfo.hasPii ?? false,
        hasSensitivePii: application.basicInfo.hasSensitivePii ?? false,
        endDate,
        ...application.basicInfo.billingProjectId
        && { projectId: application.basicInfo.billingProjectId },
      },
      infraConfig: {
        isCodeRepositoryNeeded: isCodeRepositoryNeeded(application),
        githubRepo: getGitHubRepoIfNeeded(application),
        isCiCdPipelineNeeded: isCiCdPipelineNeeded(application),
        isHostingServiceNeeded: isHostingServiceNeeded(application),
        cloudProjectNames: getGcpProjectNamesIfNeeded(application),
        comment: application.infraConfig.comment,
        githubOrganisation: teamType !== TeamType.EXPERIMENT_AND_INNOVATION
          ? getGithubOrganisationIfNeeded(application)
          : "",
      },
    });
    return service.createApplication(applicationCreationRequestData);
  },
);

const fetchMyActiveTeamsWithCategory = createAsyncThunkWithRejectValue<ITeam[], ProjectClassification>(
  "/my-teams-with-category",
  async (teamType: ProjectClassification) => service.fetchMyTeams(teamType, TeamStatus.ACTIVE),
);

const fetchMyActiveTeamsForOthers = createAsyncThunkWithRejectValue<ITeam[]>(
  "/my-teams-for-others",
  async () => service.fetchMyTeams(ProjectClassification.OTHERS, TeamStatus.ACTIVE),
);

const fetchMyActiveTeamsForGITS = createAsyncThunkWithRejectValue<ITeam[]>(
  "/my-teams-for-tech-ops",
  async () => service.fetchMyTeams(ProjectClassification.GITS, TeamStatus.ACTIVE),
);

export const getStartedPageSlice = createSlice({
  name: "getStartedPage",
  initialState,
  reducers: {
    updateGetStartedPageState: (state: IGetStartedState, action: PayloadAction<Partial<IGetStartedState>>) => ({
      ...state,
      ...action.payload,
    }),
  },
  extraReducers: {
    [experimentsAndInnovationsCategoryActions.updateInfraConfig.type]: (state, action) => {
      state.experimentsAndInnovations = experimentsAndInnovationsReducer(state.experimentsAndInnovations, action);
    },
    [experimentsAndInnovationsCategoryActions.updateBasicInfo.type]: (state, action) => {
      state.experimentsAndInnovations = experimentsAndInnovationsReducer(state.experimentsAndInnovations, action);
    },
    [OthersCategoryActions.updateBasicInfo.type]: (state, action) => {
      state.others = othersReducer(state.others, action);
    },
    [OthersCategoryActions.updateInfraConfig.type]: (state, action) => {
      state.others = othersReducer(state.others, action);
    },
    [GITSCategoryActions.updateBasicInfo.type]: (state, action) => {
      state.gits = gitsReducer(state.gits, action);
    },
    [GITSCategoryActions.updateInfraConfig.type]: (state, action) => {
      state.gits = gitsReducer(state.gits, action);
    },
    [createApplication.pending.type]: (state) => {
      state.createApplication = PEV(null, "create application is in progress");
    },
    [createApplication.fulfilled.type]: (state, action: PayloadAction<ApplicationBasicInfo, never>) => {
      state.createApplication = PEV(action.payload);
    },
    [createApplication.rejected.type]: (state, action: PayloadAction<never, never, never, Error>) => {
      state.createApplication = PEV(null, null, `Error happened in application creation ${action.error.message}`);
    },
    [fetchMyActiveTeamsWithCategory.pending.type]: (state) => {
      state.teamListForSelectedCategoryPEV = PEV(state.teamListForSelectedCategoryPEV.value, "Fetching Associated teams");
    },
    [fetchMyActiveTeamsWithCategory.fulfilled.type]: (state, action: PayloadAction<ITeam[], never>) => {
      state.teamListForSelectedCategoryPEV = PEV(action.payload);
    },
    [fetchMyActiveTeamsWithCategory.rejected.type]: (state, action: PayloadAction<NeoError>) => {
      state.teamListForSelectedCategoryPEV = PEV([], null, action.payload.getErrorInfo());
    },
    [fetchMyActiveTeamsForOthers.pending.type]: (state) => {
      state.teamListForOthersPEV = PEV(state.teamListForOthersPEV.value, "Fetching Associated teams");
    },
    [fetchMyActiveTeamsForOthers.fulfilled.type]: (state, action: PayloadAction<ITeam[], never>) => {
      state.teamListForOthersPEV = PEV(action.payload);
    },
    [fetchMyActiveTeamsForOthers.rejected.type]: (state, action: PayloadAction<NeoError>) => {
      state.teamListForOthersPEV = PEV([], null, action.payload.getErrorInfo());
    },
    [fetchMyActiveTeamsForGITS.pending.type]: (state) => {
      state.teamListForGITSPEV = PEV(state.teamListForGITSPEV.value, "Fetching Associated teams");
    },
    [fetchMyActiveTeamsForGITS.fulfilled.type]: (state, action: PayloadAction<ITeam[], never>) => {
      state.teamListForGITSPEV = PEV(action.payload);
    },
    [fetchMyActiveTeamsForGITS.rejected.type]: (state, action: PayloadAction<NeoError>) => {
      state.teamListForGITSPEV = PEV([], null, action.payload.getErrorInfo());
    },
  },
});

export const getStartedPageReducer = getStartedPageSlice.reducer;

const updateGetStartedPageState: ActionCreatorWithPayload<Partial<IGetStartedState>>
    = getStartedPageSlice.actions.updateGetStartedPageState;

const updateCurrentStep = (currentStep: CurrentStep) => updateGetStartedPageState({
  currentStep,
});

const updateShowCreateApplicationNotApplicablePage = (showCreateApplicationNotApplicablePage: boolean) => updateGetStartedPageState({
  showCreateApplicationNotApplicablePage,
});

const updateSelectedProjectClassification
    = (selectedProjectClassification: ProjectClassification, nextStep: CurrentStep = "BASIC_INFO") =>
      updateGetStartedPageState({
        selectedProjectClassification,
        currentStep: nextStep,
      });

const resetState = () => updateGetStartedPageState({
  experimentsAndInnovations: experimentsAndInnovationsInitialState,
  others: othersInitialState,
  gits: gitsInitialState,
  createApplication: PEV(null),
  selectedProjectClassification: ProjectClassification.NONE,
  currentStep: "PROJECT_CLASSIFICATION",
});

export const GetStartedPageActions = {
  updateCurrentStep,
  updateSelectedProjectClassification,
  resetState,
  createApplication,
  fetchMyActiveTeamsWithCategory,
  updateShowCreateApplicationNotApplicablePage,
  fetchMyActiveTeamsForOthers,
  fetchMyActiveTeamsForGITS,
};
